import { z } from "zod";

import {
  EQUAL_PASSWORD_MESSAGE,
  INVALID_OPTION_MESSAGE,
  isValidCNPJ,
  isValidCPF,
  MAX_LENGTH_MESSAGE,
  MIN_LENGTH_MESSAGE,
  PASSWORD_RULE_MESSAGE,
  REQUIRED_MESSAGE,
} from "@/utils";

const validationMap = {
  PERSON: {
    validator: isValidCPF,
    errorMessage: "Invalid CPF",
  },
  COMPANY: {
    validator: isValidCNPJ,
    errorMessage: "Invalid CNPJ",
  },
};

export const profileSchema = z
  .object({
    name: z
      .string()
      .min(1, { message: MIN_LENGTH_MESSAGE("1") })
      .max(150, { message: MAX_LENGTH_MESSAGE("150") }),
    mfaEnabled: z.coerce.number().default(0),
    mfaMethod: z
      .enum(["EMAIL", "APP"], {
        message: INVALID_OPTION_MESSAGE,
      })
      .optional()
      .nullable(),
    mfaKey: z.object({
      secret: z.string(),
      url: z.string(),
    }),
    picturePath: z.string().optional(),
    file: z.instanceof(File).optional(),
    whatsapp: z
      .string()
      .min(13, { message: MIN_LENGTH_MESSAGE("10") })
      .max(14, { message: MAX_LENGTH_MESSAGE("15") })
      .transform((val) => val.replace(/\D/g, "")),
    personType: z.enum(["COMPANY", "PERSON"], {
      message: INVALID_OPTION_MESSAGE,
    }),
    document: z
      .string()
      .trim()
      .transform((val) => val.replace(/\D/g, "")),
  })

  .superRefine((data, ctx) => {
    const validation = validationMap[data.personType];

    if (validation) {
      if (!validation.validator(data.document)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: validation.errorMessage,
          path: ["document"],
        });
      }
    }
    if (data.mfaEnabled === 1 && !data.mfaMethod) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: REQUIRED_MESSAGE,
        path: ["mfaMethod"],
      });
    }
  });

export const passwordSchema = z
  .object({
    currentPassword: z.string().min(1, { message: REQUIRED_MESSAGE }),
    newPassword: z
      .string()
      .min(6, { message: MIN_LENGTH_MESSAGE("6") })
      .regex(/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{6,}$/, {
        message: PASSWORD_RULE_MESSAGE,
      }),
    confirmPassword: z
      .string()
      .min(6, { message: MIN_LENGTH_MESSAGE("6") })
      .regex(/^(?=.*[a-zA-Z])(?=.*\d)(?=.*[^a-zA-Z0-9]).{6,}$/, {
        message: PASSWORD_RULE_MESSAGE,
      }),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: EQUAL_PASSWORD_MESSAGE,
    path: ["confirmPassword"],
  });
